import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';

const ReadingPart4 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        setAnswers(migratedAnswers);
        setIsHydrated(true);
        return;
      }

      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData) {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId || isReviewMode) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated, isReviewMode]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const {
    image_url,
    option_bank,
    reading_text,
    story_title
  } = exerciseData.content_data;

  const questions = [...exerciseData.questions].sort((a, b) => a.order_index - b.order_index);
  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const renderStory = () => {
    const parts = reading_text.split(/(\{\{\d+\}\}|\|\\\\[\\\\[\d+\\\]\\\\\\])/g);
    return parts.map((part, index) => {
      const match = part.match(/\{\{(\d+)\}\}|\|\\\\[\\\\[(\d+)\\\]\\\\\\]/);
      if (match) {
        const qNumber = parseInt(match[1] || match[2], 10);
        const question = questions.find(q => q.order_index === qNumber);

        if (!question) return part;

        if (qNumber === 0) {
          return <span key={index} className="exercise-example-text mx-1 font-bold text-primary-blue underline decoration-2 underline-offset-4">{question.accepted_answers[0]}</span>;
        }

        const isAnswerCorrect = isReviewMode && (question.is_correct === true || question.is_correct === 'true');
        const showIncorrectAnswerInReview = isReviewMode && !isAnswerCorrect;

        return (
          <span key={index} className="inline-flex items-baseline gap-1">
            <strong className="exercise-gap-number align-middle">({qNumber})</strong>
            {showIncorrectAnswerInReview ? (
              <span className="inline-flex items-center bg-red-50 dark:bg-red-900/20 px-2 py-0.5 rounded-lg border border-red-100 dark:border-red-800 mx-1 align-middle">
                <span className="text-red-500 line-through decoration-2 mr-2 font-bold">{question.user_answer?.answer || '(empty)'}</span>
                <span className="text-green-600 font-bold">{question.accepted_answers[0]}</span>
              </span>
            ) : (
              <input
                type="text"
                autoComplete="off"
                value={(showHint || isAnswerCorrect) ? question.accepted_answers[0] : (answers[question.id] || '')}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                className={`exercise-input-field exercise-input-text !w-[100px] text-sm px-1 py-1 text-center align-middle ${showHint ? 'hint-input' : ''}`}
                readOnly={showHint || isReviewMode || !isHydrated}
                disabled={isReviewMode || !isHydrated}
              />
            )}
            {isReviewMode && (isAnswerCorrect
              ? <span className="correct-checkmark align-middle scale-75">✓</span>
              : <span className="incorrect-checkmark align-middle scale-75">✗</span>
            )}
          </span>
        );
      }

      return part;
    });
  };
  return (
    <div className="w-full h-full flex flex-col justify-center max-w-6xl mx-auto space-y-3">
      <div className="bg-white dark:bg-card-dark p-2 rounded-2xl shadow-soft border-2 border-white dark:border-card-dark overflow-hidden shrink-0">
        {image_url && (
          <img
            alt="Story illustration"
            className="mx-auto rounded-xl w-auto max-w-xl max-h-40 object-contain shadow-sm"
            src={`${ASSET_BASE_URL}${image_url}`}
          />
        )}
      </div>
      <div className="exercise-story-box text-center relative z-10 flex-1 min-h-0 overflow-y-auto custom-scrollbar p-4">
        {story_title && <h3 className="exercise-story-title mb-2 text-lg">{story_title}</h3>}
        <div className="exercise-body-text whitespace-pre-wrap leading-snug text-sm text-left">
          {renderStory()}
        </div>
      </div>
    </div>
  );
};

export default ReadingPart4;